import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CardContent, CardHeader } from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Separator } from "@/components/ui/separator";
import {
  ChevronDown,
  ChevronUp,
  Clock,
  ExternalLink,
  ThumbsUp,
  ThumbsDown,
  AlertTriangle,
  CheckCircle,
  ArrowUpRight,
  Wrench,
  XCircle,
  Maximize2,
  Minimize2,
} from "lucide-react";

interface TicketSummary {
  id: string;
  title: string;
  category: string;
  description: string;
  resolution: string;
  status: "resolved" | "escalated" | "workaround" | "unresolved";
  sentiment: "positive" | "negative" | "neutral";
  closedDate: string;
  daysAgo: number;
  isRepeatedIssue?: boolean;
  agentName: string;
}

interface SupportTicketHistoryProps {
  customerName: string;
  tickets: TicketSummary[];
  totalTickets: number;
}

const statusConfig = {
  resolved: {
    icon: CheckCircle,
    color: "bg-green-100 text-green-800 border-green-200",
    label: "Resolved",
  },
  escalated: {
    icon: ArrowUpRight,
    color: "bg-orange-100 text-orange-800 border-orange-200",
    label: "Escalated",
  },
  workaround: {
    icon: Wrench,
    color: "bg-blue-100 text-blue-800 border-blue-200",
    label: "Workaround",
  },
  unresolved: {
    icon: XCircle,
    color: "bg-red-100 text-red-800 border-red-200",
    label: "Unresolved",
  },
};

const sentimentConfig = {
  positive: { icon: ThumbsUp, color: "text-green-600" },
  negative: { icon: ThumbsDown, color: "text-red-600" },
  neutral: { icon: Clock, color: "text-gray-500" },
};

export function SupportTicketHistory({
  customerName,
  tickets,
  totalTickets,
}: SupportTicketHistoryProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedTickets, setExpandedTickets] = useState<Set<string>>(
    new Set([tickets[0]?.id])
  );
  const [isCardCollapsed, setIsCardCollapsed] = useState(false);

  const toggleTicketExpansion = (ticketId: string) => {
    const newExpanded = new Set(expandedTickets);
    if (newExpanded.has(ticketId)) {
      newExpanded.delete(ticketId);
    } else {
      newExpanded.add(ticketId);
    }
    setExpandedTickets(newExpanded);
  };

  const formatTimeAgo = (daysAgo: number) => {
    if (daysAgo === 0) return "Today";
    if (daysAgo === 1) return "Yesterday";
    if (daysAgo < 7) return `${daysAgo} days ago`;
    if (daysAgo < 30) return `${Math.floor(daysAgo / 7)} weeks ago`;
    return `${Math.floor(daysAgo / 30)} months ago`;
  };

  const visibleTickets = isExpanded ? tickets : tickets.slice(0, 2);
  const hasRepeatedIssues = tickets.some((ticket) => ticket.isRepeatedIssue);

  return (
    <Collapsible
      open={!isCardCollapsed}
      onOpenChange={(open) => setIsCardCollapsed(!open)}
      className="w-full  border rounded-lg bg-card text-card-foreground shadow-sm transition-all duration-200"
    >
      <div className="flex items-center justify-between p-4 border-b">
        <div className="flex items-center gap-2">
          <h4 className="font-semibold">Support History</h4>
          {hasRepeatedIssues && (
            <Badge
              variant="outline"
              className="bg-amber-50 text-amber-700 border-amber-200"
            >
              <AlertTriangle className="h-3 w-3 mr-1" />
              Pattern Detected
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">
            Found {totalTickets} tickets
          </span>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              {isCardCollapsed ? (
                <Maximize2 className="h-4 w-4" />
              ) : (
                <Minimize2 className="h-4 w-4" />
              )}
            </Button>
          </CollapsibleTrigger>
        </div>
      </div>

      <CollapsibleContent>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">{customerName}</div>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
          </div>

          {hasRepeatedIssues && (
            <div className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded-md">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <span className="text-sm text-amber-800 font-medium">
                Pattern detected: Similar to current ticket
              </span>
            </div>
          )}
        </CardHeader>

        <CardContent className="space-y-4">
          {visibleTickets.map((ticket, index) => {
            const StatusIcon = statusConfig[ticket.status].icon;
            const SentimentIcon = sentimentConfig[ticket.sentiment].icon;
            const isExpanded = expandedTickets.has(ticket.id);

            return (
              <div key={ticket.id} className="space-y-3">
                <Collapsible
                  open={isExpanded}
                  onOpenChange={() => toggleTicketExpansion(ticket.id)}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex flex-col items-center">
                      <div className="w-2 h-2 bg-primary rounded-full mt-2" />
                      {index < visibleTickets.length - 1 && (
                        <div className="w-px h-16 bg-border mt-2" />
                      )}
                    </div>

                    <div className="flex-1 space-y-2">
                      <CollapsibleTrigger asChild>
                        <Button
                          variant="ghost"
                          className="w-full justify-start p-0 h-auto text-left"
                        >
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium text-sm">
                                {ticket.title}
                              </h4>
                              {ticket.isRepeatedIssue && (
                                <Badge
                                  variant="outline"
                                  className="text-xs bg-amber-50 text-amber-700 border-amber-200"
                                >
                                  Repeat
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <Clock className="h-3 w-3" />
                              <span>
                                Closed {formatTimeAgo(ticket.daysAgo)}
                              </span>
                            </div>
                          </div>
                        </Button>
                      </CollapsibleTrigger>

                      <div className="flex items-center gap-2">
                        <Badge
                          variant="outline"
                          className={`text-xs ${
                            statusConfig[ticket.status].color
                          }`}
                        >
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {statusConfig[ticket.status].label}
                        </Badge>

                        <div className="flex items-center gap-1">
                          <SentimentIcon
                            className={`h-3 w-3 ${
                              sentimentConfig[ticket.sentiment].color
                            }`}
                          />
                          <span className="text-xs text-muted-foreground capitalize">
                            {ticket.sentiment}
                          </span>
                        </div>
                      </div>

                      <CollapsibleContent className="space-y-3">
                        <div className="text-sm space-y-2">
                          <div>
                            <span className="font-medium text-muted-foreground">
                              Issue:{" "}
                            </span>
                            <span>{ticket.description}</span>
                          </div>
                          <div>
                            <span className="font-medium text-muted-foreground">
                              Resolution:{" "}
                            </span>
                            <span>{ticket.resolution}</span>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Handled by {ticket.agentName}
                          </div>
                        </div>
                      </CollapsibleContent>
                    </div>
                  </div>
                </Collapsible>

                {index < visibleTickets.length - 1 && (
                  <Separator className="my-4" />
                )}
              </div>
            );
          })}

          {tickets.length > 2 && (
            <div className="pt-2">
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => setIsExpanded(!isExpanded)}
              >
                {isExpanded ? (
                  <>
                    <ChevronUp className="h-4 w-4 mr-2" />
                    Show Less
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4 mr-2" />
                    Show {tickets.length - 2} More Tickets
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </CollapsibleContent>
    </Collapsible>
  );
}
